import { useState, type ChangeEvent } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { consultationConditionLabel, useAuth } from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi } from "@/lib/callables";

// ── Main page ────────────────────────────────────────────────────────────

export default function BookingsPharmacyDetailPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const params = useParams<{ pharmacyId: string }>();
  const pharmacyId = params.pharmacyId;

  const [selectedBookingPharmacyId, setSelectedBookingPharmacyId] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [pharmacistFilter, setPharmacistFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // 1. Overview — find if we have one or multiple booking pharmacies
  const overviewQ = useQuery({
    queryKey: ["bookings", "overview", chainId],
    queryFn: () => chainAdminApi.chainAdminBookingsOverview({ chainId }),
    enabled: !!chainId,
  });

  const pharmacyOverview = overviewQ.data?.items.find((i) => i.pharmacyId === pharmacyId);

  // 2. If multiple locations, list them
  const listBookingPharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies", chainId, pharmacyId],
    queryFn: () => chainAdminApi.chainAdminListBookingPharmacies({ chainId, pharmacyId }),
    enabled: !!chainId && !!pharmacyOverview && pharmacyOverview.bookingPharmacyCount > 1,
  });

  const bookingPharmacyId =
    selectedBookingPharmacyId ||
    (pharmacyOverview?.bookingPharmacyCount === 1 ? pharmacyOverview.bookingPharmacyId : null);

  // 3. Rooms (needed to resolve room names in the bookings table)
  const roomsQ = useQuery({
    queryKey: ["bookings", "rooms", chainId, bookingPharmacyId],
    queryFn: () =>
      chainAdminApi.chainAdminListRooms({ chainId, bookingPharmacyId: bookingPharmacyId! }),
    enabled: !!chainId && !!bookingPharmacyId,
  });

  // 4. Pharmacists (needed for the pharmacist filter and to resolve names)
  const pharmacistsQ = useQuery({
    queryKey: ["bookings", "availability-pharmacists", chainId, bookingPharmacyId],
    queryFn: () =>
      chainAdminApi.chainAdminListAvailabilityPharmacists({
        chainId,
        bookingPharmacyId: bookingPharmacyId!,
      }),
    enabled: !!chainId && !!bookingPharmacyId,
  });

  // 5. Bookings list (with optional pharmacist filter)
  const bookingsQ = useQuery({
    queryKey: [
      "bookings", "list", chainId, bookingPharmacyId,
      statusFilter, pharmacistFilter, startDate, endDate,
    ],
    queryFn: () =>
      chainAdminApi.chainAdminListBookings({
        chainId,
        bookingPharmacyId: bookingPharmacyId!,
        status: statusFilter as any,
        pharmacistId: pharmacistFilter === "all" ? undefined : pharmacistFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!chainId && !!bookingPharmacyId,
  });

  const pharmacists = pharmacistsQ.data?.items ?? [];

  if (overviewQ.isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (!pharmacyOverview) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Pharmacy not found in bookings overview.</Typography>
        <Link href="/bookings">Back to bookings</Link>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={pharmacyOverview.displayName}
        description={`Bookings for pharmacy ${pharmacyId}`}
        actions={
          <Button
            component={Link}
            href="/bookings"
            startIcon={<ChevronLeftIcon />}
            variant="outlined"
            size="small"
          >
            Back to Bookings
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Location picker (multi-location pharmacies) */}
        {pharmacyOverview.bookingPharmacyCount > 1 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Select Booking Location
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              More than one booking record exists for this pharmacy. The one marked
              "Primary" is the record the patient booking link and the mobile app's
              own availability writes resolve to — pick it unless you specifically
              need to inspect an older, retired one.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {listBookingPharmaciesQ.data?.items.map((bp) => (
                <Chip
                  key={bp.id}
                  label={`${bp.name} (${bp.eircode})${bp.isPrimary ? " · Primary" : ""}`}
                  onClick={() => setSelectedBookingPharmacyId(bp.id)}
                  color={bookingPharmacyId === bp.id ? "primary" : bp.isPrimary ? "success" : "default"}
                  variant={bookingPharmacyId === bp.id ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </Paper>
        )}

        {!bookingPharmacyId ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">Please select a booking location.</Typography>
          </Paper>
        ) : (
          <Paper>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Bookings
                {pharmacistFilter !== "all" && (
                  <Chip
                    size="small"
                    sx={{ ml: 1 }}
                    label={
                      pharmacists.find((p) => p.pharmacistId === pharmacistFilter)
                        ?.displayName ?? "Filtered"
                    }
                    onDelete={() => setPharmacistFilter("all")}
                  />
                )}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="rescheduled">Rescheduled</MenuItem>
                    <MenuItem value="no_show">No show</MenuItem>
                  </Select>
                </FormControl>
                {pharmacists.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Pharmacist</InputLabel>
                    <Select
                      value={pharmacistFilter}
                      label="Pharmacist"
                      onChange={(e) => setPharmacistFilter(e.target.value)}
                    >
                      <MenuItem value="all">All pharmacists</MenuItem>
                      {pharmacists.map((p) => (
                        <MenuItem key={p.pharmacistId} value={p.pharmacistId}>
                          {p.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Room</TableCell>
                    <TableCell>Pharmacist</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Patient Email</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookingsQ.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Loading bookings…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : !bookingsQ.data?.items.length ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No bookings found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookingsQ.data.items.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {b.date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {b.startTime} - {b.endTime}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {roomsQ.data?.items.find((r) => r.id === b.roomId)?.name || b.roomId}
                        </TableCell>
                        <TableCell>
                          {pharmacists.find((p) => p.pharmacistId === b.pharmacistId)
                            ?.displayName ||
                            (b.pharmacistId
                              ? b.pharmacistId.slice(0, 8) + "…"
                              : "—")}
                        </TableCell>
                        <TableCell>
                          {b.condition ? consultationConditionLabel(b.condition) : "—"}
                        </TableCell>
                        <TableCell>{b.patientEmail}</TableCell>
                        <TableCell>
                          <Chip
                            label={b.status.toUpperCase()}
                            size="small"
                            color={
                              b.status === "confirmed"
                                ? "success"
                                : b.status === "pending"
                                ? "warning"
                                : b.status === "completed"
                                ? "info"
                                : "default"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </>
  );
}
